import React, { Component } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import sortBy from 'array-sort-by'
import { pxToRem, color } from '../../utils'
import { SimpleLoader } from '../loaders'
import { RefreshIcon } from '../icons'
import { Button } from '../Form'
import TablePagination from './TablePagination'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: ${props => (props.width ? pxToRem(props.width) : '100%')};
  border-radius: ${pxToRem('7px')} ${pxToRem('7px')} 0 0;
  overflow: hidden;
  ${({ loading, error }) =>
    loading || error
      ? `
    min-width: auto;
  `
      : ''};
`

const WrapperTable = styled.div`
  overflow: auto;
  ${({ loading, error }) =>
    loading || error
      ? `
    overflow: hidden;
  `
      : ''};
`

const ReloadButton = styled(Button)`
  background: transparent;
  font-size: ${pxToRem(12)};
  padding: ${pxToRem(8)} ${pxToRem(15)};
  display: flex;
  border-color: ${color.primary};
  color: ${color.primary};
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin: ${pxToRem(15)} auto ${pxToRem(10)};
  svg {
    margin-left: ${pxToRem(10)};
  }
`

const TableError = styled.div`
  background: #fefefe;
  color: #5b666d;
  min-height: ${pxToRem(140)};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  div {
    text-align: center;
  }
  strong {
    font-size: ${pxToRem(14)};
    text-align: center;
  }
`

const WithoutItems = styled.div`
  background: #fefefe;
  color: #5b666d;
  min-height: ${pxToRem(140)};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  div {
    text-align: center;
  }
  strong {
    font-size: ${pxToRem(14)};
    text-align: center;
  }
`

const Table = styled.table`
  min-width: ${props => (props.width ? pxToRem(props.width) : '100%')};
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-family: 'Open Sans', sans-serif;
  background: #fff;

  /* Styles */
  thead {
    background: #dadfe2;
    text-transform: uppercase;
    border-bottom: 1px solid #d8d8d8;
  }
  th {
    color: #5b666d;
    font-size: ${pxToRem(13)};
    font-weight: bold;
    padding: ${pxToRem(15)} ${pxToRem(20)};
  }

  td {
    font-size: ${pxToRem(13)};
    text-align: left;
    padding: ${pxToRem(15)} ${pxToRem(20)};
    color: #5b666d;
  }

  tr:not(:last-child) {
    border-bottom: 1px solid #d8d8d8;
  }
`
const ThLabelSortable = styled.span`
  ${props =>
    !!props.sortable &&
    `cursor: pointer;
    position: relative;
    display: block;`},
    span {
      text-align: ${props => props.align || 'left'};
    }
`

const ThLabel = styled.span`
  display: block;
  text-align: ${props => props.align || 'left'};
  span {
    text-align: ${props => props.align || 'left'};
  }
`

const WrapperLoader = styled.div`
  background: #fff;
  height: ${pxToRem(100)};
  position: relative;
  width: 100%;
`

const TableHeader = styled.div`
  overflow: hidden;
  margin: 0;
  z-index: 999;
`

const TableContent = styled.div`
  ${props =>
    `max-height: ${props.height};
    overflow-y: auto;`}
`

const SortIndicator = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  margin: auto ${pxToRem('10px')} auto auto;
  transition: transform 0.2s;

  &::before {
    content: '';
    display: inline-block;
    width: 2px;
    height: 10px;
    position: absolute;
    left: 0;
    bottom: 0;
    background: #848d93;
    border-radius: 5px;
  }

  &::after {
    content: '';
    display: inline-block;
    width: 10px;
    height: 2px;
    position: absolute;
    right: 0;
    bottom: 0;
    background: #848d93;
    border-radius: 5px;
  }

  ${({ reverseSort }) => {
    return reverseSort
      ? `
    transform: rotate(-45deg);
    `
      : `
    transform: rotate(136deg);
    `
  }};
`

class DataTable extends Component {
  constructor(props) {
    super(props)
    if (props.defaultSort) {
      this.state = {
        data: props.data.item || [],
        pagination: props.data.pagination,
        sortByColumn: (props.pagination || {}).orderBy || props.defaultSort.key,
        reverseSort: (props.pagination || {}).order || props.defaultSort.reverse,
        rowsPerPage: 10,
        page: 1
      }
    } else {
      this.state = {
        sortByColumn: Object.entries(this.props.columns)[0][0],
        reverseSort: false
      }
    }
  }

  static propTypes = {
    data: PropTypes.array,
    columns: PropTypes.object
  }

  handleSortableClick = key => {
    this.setState(({ sortByColumn, reverseSort }) => {
      if (sortByColumn === key) {
        return {
          reverseSort: !reverseSort
        }
      } else {
        return {
          sortByColumn: key,
          reverseSort: false
        }
      }
    })
    
    !!this.props.pagination
      ? this.props.onChangeOrderBy(
          key,
          this.state.reverseSort ? 'asc' : 'desc',
          1,
          this.props.pagination.rowsPerPage
        )
      : null
  }


  sortedColumns = ({ mapedColumns = [], sortByColumn, reverseSort }) => {
    const { columns } = this.props
    const { sort = null } = columns[sortByColumn]

    if (sort) {
      return sort({ mapedColumns, sortByColumn, reverseSort })
    } else {
      return sortBy(mapedColumns, item => {
        return reverseSort ? `desc:${item[sortByColumn]}` : item[sortByColumn]
      })
    }
  }

  renderTableContent = () => {
    const {
      columns,
      data = [],
      height = 'auto',
      width,
      fetchData,
      error = false,
      pagination
    } = this.props
    
    if (error) {
      return (
        <TableError>
          <div>
            <strong>Falha ao carregar as informações.</strong>
            <br />
            <ReloadButton onClick={fetchData}>
              Tentar novamente{' '}
              <RefreshIcon></RefreshIcon>
            </ReloadButton>
          </div>
        </TableError>
      )
    }

    const { sortByColumn, reverseSort } = this.state
    const { sortedColumns } = this

    const mapedColumns = data.reduce((initial, row) => {
      const obj = {}

      for (let column of Object.keys(columns)) {
        const realValue = !!(columns[column] || {})['realValue']
        obj[column] = realValue ? row[column] : (row[column] || '---')
      }

      return [...initial, obj]
    }, [])

    const columnsToRender = this.props.pagination ? 
      mapedColumns
      : 
      sortedColumns({ sortByColumn, reverseSort, mapedColumns })

    return !(data || []).length ? (
      <WithoutItems>
        <div>
          <strong>Não há informações.</strong>
        </div>
      </WithoutItems>
    ) : (
      <TableContent height={height}>
        <Table width={width}>
          <tbody>
            {columnsToRender.map((row, index) => {
              return (
                <tr key={index}>
                  {Object.entries(row).map(column => {
                    const CellComponent = columns[column[0]]['CellComponent']
                    const hide = columns[column[0]]['hide']
                    const format = columns[column[0]]['format']

                    if (hide) {
                      return null
                    }

                    return CellComponent ? (
                      <td key={column[0]} width={columns[column[0]].width || 'auto'}>
                        <CellComponent name={column[0]} value={column[1]} row={row} />
                      </td>
                    ) : (
                      <td key={column[0]} width={columns[column[0]].width || 'auto'}>
                        {format ? format(column[1]) : column[1]}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </Table>
      </TableContent>
    )
  }

  renderLoader = () => {
    return (
      <WrapperLoader>
        <SimpleLoader />
      </WrapperLoader>
    )
  }

  render() {
    const { handleSortableClick, renderTableContent, renderLoader } = this
    const {
      columns,
      width,
      loading = false,
      error,
      colSpan,
      data,
      pagination = {},
      labelPages,
      labelRowsPerPage
    } = this.props
    const { sortByColumn, reverseSort } = this.state
    const { rowsPerPage, currentPage, totalPages, orderBy } = pagination
    return (
      <WrapperTable loading={loading} error={error}>
        <Wrapper width={width} loading={loading} error={error}>
          <TableHeader>
            <Table width={width}>
              <thead>
                <tr>
                  {Object.entries(columns).map(column => {
                    const [key, obj] = column
                    const { HeaderComponent, hide } = obj

                    if (hide) {
                      return null
                    }

                    if (HeaderComponent) {
                      return (
                        <th key={key} width={obj.width}>
                          <HeaderComponent
                            name={key}
                            value={obj}
                            sortByColumn={sortByColumn}
                            handleSortableClick={handleSortableClick}
                            reverseSort={reverseSort}
                          />
                        </th>
                      )
                    }
                    return (
                      <th key={key} width={obj.width}>
                        {obj.sortable ? (
                          <ThLabelSortable
                            sortable={true}
                            align={obj.align}
                            onClick={() => {
                              handleSortableClick(key)
                            }}
                          >
                            <span>{obj.label}</span>
                            {key === sortByColumn ? (
                              <SortIndicator
                                className="exo-data-table-sort-indicator"
                                reverseSort={reverseSort}
                              />
                            ) : null}
                          </ThLabelSortable>
                        ) : (
                          <ThLabel align={obj.align}>
                            <span>{obj.label}</span>
                          </ThLabel>
                        )}
                      </th>
                    )
                  })}
                </tr>
              </thead>
            </Table>
          </TableHeader>
          {loading ? renderLoader() : renderTableContent()}
          <Table width={width}>
            {data.length && !!pagination ? (
              <TablePagination
                rowsPerPageOptions={[
                  { value: 10, label: 10 },
                  { value: 20, label: 20 },
                  { value: 50, label: 50 },
                  { value: 100, label: 100 }
                ]}
                colSpan={colSpan}
                onChangePage={(newPage, rowsPerPage) =>
                  this.props.onChangePage(newPage, rowsPerPage)
                }
                onChangeRowsPerPage={(rowsPerPage, currentPage) =>
                  this.props.onChangeRowsPerPage(rowsPerPage, currentPage)
                }
                labelRowsPerPage={labelRowsPerPage}
                labelPages={labelPages}
                rowsPerPage={rowsPerPage}
                currentPage={currentPage}
                totalPages={totalPages}
              />
            ) : null}
          </Table>
        </Wrapper>
      </WrapperTable>
    )
  }
}

export default DataTable



// WEBPACK FOOTER //
// ./src/components/Table/Table.js